/**
 * Header 26 — to-række header, én mega-dropdown pr. kategori (september 2026)
 * Baseret på header-v2.js (inkl. den løbende 350ms/minChars-2-rettelse fra
 * en parallel session, se docs/HEADER_FOOTER_2026_09.md "Header 26 / 4a").
 * Samme backend: https://backend.jmdimensions.com/api/search/advanced
 *
 * Vigtigste forskelle fra header-v2.js:
 *  - N mega-paneler (ét pr. kategori) i stedet for ét fælles "Alle
 *    produkter"-panel: hover-intent-forsinkelse, diagonal-bevægelse mod
 *    kortet, klik navigerer (rigtige kategori-links), Esc returnerer fokus.
 *  - Ingen JS-positionering af panelerne (position:absolute i CSS'en i
 *    stedet for fixed+beregnet top) — positionMega()/positionSearchDropdown()
 *    og deres scroll/resize-gentegning er væk.
 *  - 4-korts søge-grid i stedet for en liste; interpretation læses fra
 *    data.analysis.query_interpretation (IKKE data.meta.interpretation, som
 *    backend'en aldrig sender).
 *  - Editor-robusthed: global boot-guard (shopify:section:load re-kører
 *    <script>-taggen, ikke kun init()), settings læst INDE i init() (ikke
 *    en gang for alle ved parse-tid), shopify:block:select åbner og PINNER
 *    den rigtige dropdown/accordion-række.
 *  - ResizeObserver på --height-header (ikke kun 'load').
 *  - Backend'en RANGERER kun; søgekortenes billede, titel, handle og pris
 *    hentes fra produktet via Storefront API'et (2026-10-27, se
 *    "Søgekortene viser PRODUKTET" nedenfor).
 */

(function () {
  'use strict';

  /* Editor-genindlæsninger (shopify:section:load) udfører HELE <script>-
     taggen igen, ikke kun en fælles init() — uden denne vagt ville hvert
     re-render af sektionen tilføje endnu et sæt document/window-listeners
     (dobbelt-toggling af accordion-rækker, dobbelt scroll-håndtering). */
  if (window.__hv26Boot) return;
  window.__hv26Boot = true;

  /* ─── Config ─── */
  const CONFIG = {
    apiUrl: 'https://backend.jmdimensions.com',
    endpoint: '/api/search/advanced',
    currency:
      typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active
        ? Shopify.currency.active
        : 'DKK',
    maxHistoryItems: 5,
    megaOpenDelay: 130,
    megaSwitchDelay: 90,
    megaCloseDelay: 120,
    // 300ms/1 tegn loggede hver eneste as-you-type-keystroke som en søgning
    // (2026-09: 78% af alle logs var præfikser af en efterfølgende søgning,
    // "c"/"la"/"sko" toppede "mest søgte") — se JMD-backend's
    // lib/search-log.ts for kollaps-logikken på backend-siden af samme fix.
    searchDebounce: 350,
    minChars: 2,
    // Søgekortenes visningsdata (billede, titel, handle, pris) hentes fra
    // produktet via Storefront API'et — se "Søgekortene viser PRODUKTET"
    // nedenfor og docs/HEADER_FOOTER_2026_09.md (2026-10-27).
    liveEndpoint: '/api/2026-07/graphql.json',
    // ALLE rangerede produkter slås op i det ene kald (loft 50): så er
    // "N resultater" eksakt, og der vises 4 kort, selv når mange af de
    // øverste er arkiveret/afpubliceret. Målt 2026-10-27: kaldet koster 4
    // point og tager ~100 ms (median, varm forbindelse) uanset 8, 20 eller
    // 50 id'er; 50 id'er er ~4 KB gzip.
    liveLookupCount: 50,
    liveTimeout: 1200,
    liveCacheMs: 5 * 60 * 1000,
  };

  /* Breakpoint matcher header-26.css' ene layout-skift (## Responsivt). */
  const DRAWER_BP = 1023;

  /* ─── State ─── */
  const state = {
    isSearchOpen: false,
    openMegaItem: null,
    megaOpenedByHover: false,
    searchQuery: '',
    // Søgeordet hvis resultater står (eller er på vej) i desktop-grid'et —
    // så en genåbning kan vise dem i stedet for tom-tilstanden.
    shownQuery: '',
    searchHistory: [],
    trendingSearches: [],
    megaCloseTimer: null,
    megaOpenTimer: null,
    mobileMenuOpen: false,
    pinnedBlockId: null,
  };
  try {
    state.searchHistory = JSON.parse(localStorage.getItem('hv2SearchHistory') || '[]');
  } catch (e) { /* korrupt localStorage-værdi må ikke fælde hele scriptet */ }

  /* ─── DOM Elements ─── */
  const el = {};
  let LABELS = {};

  function cacheElements() {
    el.header = document.querySelector('.header-v2');
    el.searchWrap = document.querySelector('.hv2-search-wrap');
    el.searchInner = document.querySelector('.hv2-search-inner');
    el.searchInput = document.querySelector('[data-hv2-search-input]');
    el.searchDropdown = document.querySelector('[data-hv2-search-dropdown]');
    el.backdrop = document.querySelector('[data-hv2-backdrop]');
    el.animatedPlaceholder = document.querySelector('[data-hv2-animated-placeholder]');

    el.defaultView = document.querySelector('[data-hv2-default-view]');
    el.searchView = document.querySelector('[data-hv2-search-view]');
    el.suggestionsContainer = document.querySelector('[data-hv2-suggestions]');
    el.ddHead = document.querySelector('[data-hv2-dd-head]');
    el.ddEyebrow = document.querySelector('[data-hv2-dd-eyebrow]');
    el.interpretation = document.querySelector('[data-hv2-interpretation]');
    el.resultsCount = document.querySelector('[data-hv2-results-count]');
    el.productGrid = document.querySelector('[data-hv2-product-grid]');
    el.seeAll = document.querySelector('[data-hv2-see-all]');

    el.megaItems = Array.prototype.slice.call(document.querySelectorAll('[data-hv2-mega-item]'));

    el.mobileOverlay = document.querySelector('[data-hv2-mobile-overlay]');
    el.mobileMenuToggle = document.querySelector('[data-hv2-menu-toggle]');
    el.mobileClose = document.querySelector('[data-hv2-mobile-close]');
    el.mobileSearchInput = document.querySelector('[data-hv2-mobile-search-input]');
    el.mobileResultsContainer = document.querySelector('[data-hv2-mobile-results]');
    el.mobileResultsCount = document.querySelector('[data-hv2-mobile-results-count]');
    el.mobileDefaultView = document.querySelector('[data-hv2-mobile-default]');
    el.mobileSearchView = document.querySelector('[data-hv2-mobile-search-view]');
    el.mobileDdHead = document.querySelector('[data-hv2-mobile-dd-head]');
    el.mobileDdEyebrow = document.querySelector('[data-hv2-mobile-dd-eyebrow]');
    el.mobileInterpretation = document.querySelector('[data-hv2-mobile-interpretation]');
    el.mobileSeeAll = document.querySelector('[data-hv2-mobile-see-all]');
    el.searchTrigger = document.querySelector('[data-hv2-search-trigger]');
  }

  /* ─── Utilities ─── */
  function escapeHtml(text) {
    if (!text) return '';
    return String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function isMobile() {
    return window.matchMedia('(max-width: ' + DRAWER_BP + 'px)').matches;
  }

  /* ─── Search History ─── */
  function addToHistory(query) {
    if (!query || query.length < 2) return;
    state.searchHistory = state.searchHistory.filter(q => q.toLowerCase() !== query.toLowerCase());
    state.searchHistory.unshift(query);
    state.searchHistory = state.searchHistory.slice(0, CONFIG.maxHistoryItems);
    localStorage.setItem('hv2SearchHistory', JSON.stringify(state.searchHistory));
  }

  function clearHistory() {
    state.searchHistory = [];
    localStorage.removeItem('hv2SearchHistory');
    renderEmptyState();
  }

  window.hv2ClearHistory = clearHistory;

  function normalizeTrending(items) {
    return items.map(item => {
      if (typeof item === 'string') return { term: item, category: '' };
      return { term: item.term || '', category: item.category || '' };
    }).filter(item => item.term);
  }

  /* ─── Tom-tilstand: "Populære søgninger" + "Seneste søgninger" ─── */
  function renderEmptyState() {
    if (!el.suggestionsContainer) return;

    let html = '';
    const trending = normalizeTrending(state.trendingSearches);
    if (trending.length > 0) {
      html += `
        <div class="hv2-dd-section">
          <p class="hv2-dd-section-title">${escapeHtml(LABELS.searchPopular || 'Populære søgninger')}</p>
          <div class="hv2-dd-list">
            ${trending.slice(0, 6).map(item => `
              <button type="button" class="hv2-dd-row" data-hv2-term="${escapeHtml(item.term)}">${escapeHtml(item.term)}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    if (state.searchHistory.length > 0) {
      html += `
        <div class="hv2-dd-section">
          <div class="hv2-dd-section-head">
            <p class="hv2-dd-section-title">${escapeHtml(LABELS.searchRecent || 'Seneste søgninger')}</p>
            <button type="button" class="hv2-dd-clear" onclick="window.hv2ClearHistory()">Ryd</button>
          </div>
          <div class="hv2-dd-list">
            ${state.searchHistory.map(term => `
              <button type="button" class="hv2-dd-row" data-hv2-term="${escapeHtml(term)}">${escapeHtml(term)}</button>
            `).join('')}
          </div>
        </div>
      `;
    }

    el.suggestionsContainer.innerHTML = html;

    el.suggestionsContainer.querySelectorAll('[data-hv2-term]').forEach(btn => {
      btn.addEventListener('click', () => {
        const term = btn.dataset.hv2Term;
        if (el.searchInput) {
          el.searchInput.value = term;
          el.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
          el.searchInput.focus();
        }
      });
    });
  }

  /* ─── Session ID ─── */
  function getSessionId() {
    try {
      const stored = JSON.parse(localStorage.getItem('hv2_session') || 'null');
      if (stored && stored.id && Date.now() - stored.created < 30 * 60 * 1000) return stored.id;
    } catch (e) { /* ignore */ }
    const id = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('hv2_session', JSON.stringify({ id, created: Date.now() }));
    return id;
  }

  /* ─── AI Search API ─── */
  let searchController = null;

  function fetchSearchResults(query, signal) {
    return fetch(`${CONFIG.apiUrl}${CONFIG.endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        context: { session_id: getSessionId() },
        filters: { available_only: true },
      }),
      signal: signal,
    }).then(r => {
      if (!r.ok) throw new Error('Search API error: ' + r.status);
      return r.json();
    });
  }

  function renderDdHead(headEl, eyebrowEl, interpretationEl, interpretation, lowConfidence) {
    if (!headEl || !interpretationEl) return;
    if (!interpretation) {
      headEl.hidden = true;
      return;
    }
    if (eyebrowEl) {
      eyebrowEl.textContent = lowConfidence ? (LABELS.searchLowConf || 'Mente du') : (LABELS.searchInterpret || 'Vi forstår det som');
    }
    interpretationEl.textContent = interpretation;
    headEl.hidden = false;
  }

  /* Fælles fallback-kæde for "Vi forstår det som". Backend'en (JMD-backend
     handlers/search-advanced.ts) svarer med `analysis.query_interpretation`
     — IKKE `meta.interpretation`, som header-v2.js fejlagtigt læste (det
     felt findes aldrig i responsen, så headline'n faldt altid tilbage til
     conversational_suggestions[0] eller selve søgeordet). `meta.low_confidence`
     findes derimod rigtigt. */
  function resolveInterpretation(analysis, meta, suggestions, query) {
    var interpretation = (analysis && analysis.query_interpretation) || '';
    var lowConfidence = !!(meta && meta.low_confidence);
    if (!interpretation && suggestions && suggestions.length > 0) {
      interpretation = suggestions[0];
    }
    if (!interpretation) {
      interpretation = query;
    }
    return { interpretation: interpretation, lowConfidence: lowConfidence };
  }

  /* ─── Søgekortene viser PRODUKTET, ikke en gemt kopi (2026-10-27) ───
     Ejer-beslutning: gem identitet, slå billedet op i produktet når det
     vises. Backend'en (POST /api/search/advanced) bruges KUN til
     rangeringen — dens image_url kommer fra metadata i søgeindekset
     (Vectorize), som bevarer gamle snapshots, der aldrig slettes. Da JMD
     Image Optimizer omdøbte filerne (2026-09-30/10-01), pegede de på stier
     der svarer 404 (søgningen "eqc" gav 4 af 4 døde billeder). Optimizeren
     må gerne komprimere, erstatte og omdøbe — så må intet kort afhænge af en
     gemt billed-URL.

     Derfor: ét same-origin-kald til Shopifys Storefront API uden token
     (nodes(ids) for de rangerede product_id'er, højst liveLookupCount)
     giver handle, titel, billede og pris, som de er NU, i besøgendes
     marked (@inContext(country: Shopify.country)). null-noder (slettet,
     arkiveret, afpubliceret), noder uden onlineStoreUrl og ikke-købbare
     varer udelades; resten vises i backend'ens rækkefølge.

     Cache: product_id → node i liveCacheMs (også null), så as-you-type ikke
     henter de samme id'er igen. Et opslag der er undervejs deles mellem
     søgninger (liveInflight). Fejl caches IKKE — næste søgning prøver igen.
     Et svar med errors[] er en fejl, også når data er med: Storefront
     API'et nuller et felt det nægter adgang til (ACCESS_DENIED) og svarer
     stadig 200, og det må ikke læses som "produktet er væk".

     Timeout/fejl (liveTimeout): kortene vises stadig i backend'ens
     rækkefølge med backend'ens titel, men ALTID med pladsholderen som
     billede og UDEN pris — backend'ens image_url og pris er kopier, der kan
     være forældede (EQC stod til 299 kr. i indekset, 349 kr. i butikken). */
  const SEARCH_IMG_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f1ede3'/%3E%3Cg fill='none' stroke='%23d6ccb4' stroke-width='2.5' stroke-linejoin='round'%3E%3Crect x='33' y='36' width='34' height='28' rx='3'/%3E%3Cpath d='M37 60l9-10 7 7 4-4 6 7'/%3E%3C/g%3E%3Ccircle cx='59' cy='44' r='3' fill='%23d6ccb4'/%3E%3C/svg%3E";

  const LIVE_QUERY = 'query SoegekortLive($ids: [ID!]!, $country: CountryCode) @inContext(country: $country) { nodes(ids: $ids) { ... on Product { id handle title availableForSale onlineStoreUrl tags featuredImage { url(transform: { maxWidth: 500 }) altText } priceRange { minVariantPrice { amount currencyCode } } } } }';

  // Besøgendes land (Shopify.country, fx "DK"), så pris, valuta og
  // tilgængelighed følger samme marked som resten af siden. Ukendt ⇒ null
  // = butikkens primære marked.
  function liveCountry() {
    const c = typeof Shopify !== 'undefined' && Shopify.country ? String(Shopify.country).toUpperCase() : '';
    return /^[A-Z]{2}$/.test(c) ? c : null;
  }

  // Object.create(null): et id kan aldrig ramme prototypen.
  const liveCache = Object.create(null); // id → { t, node } (node null = findes ikke i butikken)
  const liveInflight = Object.create(null); // id → Promise<{ known, node }>
  let searchSeq = 0; // kun den seneste søgning må rendere

  // Numerisk product_id ('123' eller 'gid://shopify/Product/123'); '' hvis ukendt.
  function productIdOf(product) {
    const m = String((product && (product.product_id || product.id)) || '').match(/(\d+)$/);
    return m ? m[1] : '';
  }

  function fetchLiveNodes(ids) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), CONFIG.liveTimeout) : null;
    return fetch(CONFIG.liveEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: LIVE_QUERY, variables: { ids: ids.map(id => 'gid://shopify/Product/' + id), country: liveCountry() } }),
      signal: controller ? controller.signal : undefined,
    })
      .then(r => {
        if (!r.ok) throw new Error('Storefront API: HTTP ' + r.status);
        return r.json();
      })
      .then(json => {
        // errors[] = fejl, også med data (fx ACCESS_DENIED på ét felt, der så
        // er null) — ellers ville hvert produkt ligne "væk" i 5 min. Et
        // slettet produkt giver en null-node UDEN errors (målt 2026-10-27).
        if (json && Array.isArray(json.errors) && json.errors.length) {
          const codes = json.errors.map(e => (e && e.extensions && e.extensions.code) || (e && e.message) || '?');
          throw new Error('Storefront API: ' + codes.join(', '));
        }
        // nodes(ids) svarer i samme rækkefølge som ids. Et svar uden en node
        // pr. id er en fejl, ikke "slettet".
        const nodes = json && json.data && Array.isArray(json.data.nodes) ? json.data.nodes : null;
        if (!nodes || nodes.length !== ids.length) throw new Error('Storefront API: uventet svar');
        return nodes;
      })
      .finally(() => { if (timer) clearTimeout(timer); });
  }

  // ids → Promise<{ [id]: { known: true, node } | { known: false } }>. Afviser aldrig.
  function lookupLiveProducts(ids) {
    const now = Date.now();
    const out = Object.create(null);
    const waits = [];
    const missing = [];

    ids.forEach(id => {
      if (id in out) return;
      const hit = liveCache[id];
      if (hit && now - hit.t < CONFIG.liveCacheMs) {
        out[id] = { known: true, node: hit.node };
      } else if (liveInflight[id]) {
        out[id] = { known: false };
        waits.push(liveInflight[id].then(r => { out[id] = r; }));
      } else {
        out[id] = { known: false };
        missing.push(id);
      }
    });

    if (missing.length) {
      const request = fetchLiveNodes(missing).then(nodes => {
        const t = Date.now();
        const byId = Object.create(null);
        missing.forEach((id, i) => {
          const node = nodes[i] || null;
          liveCache[id] = { t: t, node: node };
          byId[id] = { known: true, node: node };
        });
        return byId;
      }, err => {
        if (!err || err.name !== 'AbortError') console.warn('Søgekort: Storefront-opslag fejlede', err);
        return null;
      });

      missing.forEach(id => {
        const entry = request.then(byId => (byId && byId[id]) || { known: false });
        liveInflight[id] = entry;
        entry.then(() => { if (liveInflight[id] === entry) delete liveInflight[id]; });
        waits.push(entry.then(r => { out[id] = r; }));
      });
    }

    return Promise.all(waits).then(() => out);
  }

  // Kun visning (tag kun-visning, _gpsr/PLAN.md): varen kan ikke købes, men
  // skal stadig kunne findes — kortet viser "Kun til visning" i stedet for pris.
  // Storefront API'et giver tags uden token (målt 2026-10-28).
  function isDisplayOnly(node) {
    return !!(node && Array.isArray(node.tags) && node.tags.indexOf('kun-visning') !== -1);
  }

  function isShowable(node) {
    return !!(node && node.handle && node.onlineStoreUrl && (node.availableForSale || isDisplayOnly(node)));
  }

  /* Ét billede der fejler (fx en fil der omdøbes mellem opslag og
     indlæsning) skifter til pladsholderen. data-hv2-img="placeholder" er
     endestationen — en fejl dér røres ikke, så der kan ikke opstå en løkke.
     error-events bobler ikke, men capture-fasen passerer document; bundet
     én gang takket være __hv26Boot-vagten.

     Lytteren ser kun fejl, der sker EFTER dette (deferred) script er kørt.
     Et Liquid-renderet billede (Bestsellers-kortene i tom-tilstanden, samme
     klasse), der allerede fejlede under parsingen — fx src="" — fanges
     derfor af sweepSearchImages() i init(): complete && naturalWidth 0 =
     brudt. Et lazy billede, der endnu ikke er hentet, har complete false
     og fanges af lytteren, når det fejler. */
  function swapToPlaceholder(img) {
    if (img.dataset.hv2Img === 'placeholder') return;
    img.dataset.hv2Img = 'placeholder';
    img.alt = '';
    img.src = SEARCH_IMG_PLACEHOLDER;
  }

  function onSearchImageError(e) {
    const img = e.target;
    if (!img || img.tagName !== 'IMG' || !img.classList || !img.classList.contains('hv2-search-card-img')) return;
    swapToPlaceholder(img);
  }
  document.addEventListener('error', onSearchImageError, true);

  function sweepSearchImages() {
    document.querySelectorAll('img.hv2-search-card-img').forEach(img => {
      const src = (img.getAttribute('src') || '').trim();
      if (!src || (img.complete && img.naturalWidth === 0)) swapToPlaceholder(img);
    });
  }

  /* Ny søgning tastet (skelettet vises med det samme, performSearch først
     efter debounce'en) eller feltet tømt: den igangværende søgning må ikke
     længere tegne — ellers maler den forrige søgnings sene svar sine kort,
     "Vi forstår det som" og "Se alle" hen over det nye skelet. */
  function invalidateSearch() {
    searchSeq++;
    if (searchController) {
      searchController.abort();
      searchController = null;
    }
  }

  function renderSearchMessage(container, suggestions) {
    const hint = suggestions && suggestions.length > 0
      ? `<p style="margin-top:8px;font-size:0.9em;">Prøv i stedet: <strong>${escapeHtml(suggestions[0])}</strong></p>`
      : '';
    container.innerHTML = `<div class="hv2-search-message"><p>${escapeHtml(LABELS.searchEmpty || 'Ingen resultater – prøv et andet ord')}</p>${hint}</div>`;
  }

  function performSearch(query, container, countEl, headEl, eyebrowEl, interpretationEl) {
    if (!query || !container) return;

    const seq = ++searchSeq;
    container.innerHTML = renderCardSkeleton(4);
    if (headEl) headEl.hidden = true;

    if (searchController) searchController.abort();
    searchController = new AbortController();

    fetchSearchResults(query, searchController.signal)
      .then(result => {
        if (seq !== searchSeq) return null;
        const data = result.data || {};
        const products = data.products || [];
        const suggestions = data.conversational_suggestions || [];
        const analysis = data.analysis || {};
        const meta = data.meta || {};
        const queryId = data.debug ? data.debug.query_id : null;
        const resultsWord = LABELS.searchResults || 'resultater';

        if (products.length === 0) {
          if (countEl) countEl.textContent = '0 ' + resultsWord;
          updateSeeAll(0, query);
          if (headEl) headEl.hidden = true;
          renderSearchMessage(container, suggestions);
          return null;
        }

        // Backend'en rangerer; produktet selv leverer alt der vises.
        const ranked = products.slice(0, CONFIG.liveLookupCount);
        const ids = ranked.map(productIdOf).filter(Boolean);

        return lookupLiveProducts(ids).then(live => {
          if (seq !== searchSeq) return; // en nyere søgning er startet — dens svar vinder

          let gone = 0;
          const cards = [];
          ranked.forEach(p => {
            const id = productIdOf(p);
            const entry = id ? live[id] : null;
            if (entry && entry.known) {
              if (isShowable(entry.node)) cards.push({ product: p, node: entry.node });
              else gone++;
            } else {
              cards.push({ product: p, node: null }); // ukendt (timeout/fejl): backend-tekst + pladsholder
            }
          });

          if (cards.length === 0) {
            // Samme besked overalt: "Ingen resultater" + 0, ingen "Se alle".
            if (countEl) countEl.textContent = '0 ' + resultsWord;
            updateSeeAll(0, query);
            if (headEl) headEl.hidden = true;
            renderSearchMessage(container, suggestions);
            return;
          }

          const total = Math.max(0, products.length - gone);
          if (countEl) countEl.textContent = total + ' ' + resultsWord;
          updateSeeAll(total, query);

          const resolved = resolveInterpretation(analysis, meta, suggestions, query);
          renderDdHead(headEl, eyebrowEl, interpretationEl, resolved.interpretation, resolved.lowConfidence);

          container.innerHTML = cards.slice(0, 4).map((c, i) => renderSearchCard(c.product, c.node, i, queryId)).join('');
        });
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        if (seq !== searchSeq) return;
        console.error('Search error:', err);
        if (headEl) headEl.hidden = true;
        container.innerHTML = '<div class="hv2-search-message"><p>Der skete en fejl. Prøv igen senere.</p></div>';
        updateSeeAll(0, '');
      });
  }

  /* ─── 4-korts søgegrid (H2). `node` er produktet fra Storefront API'et
     (handle, titel, billede, pris — se "Søgekortene viser PRODUKTET"
     ovenfor); null betyder at opslaget fejlede, og så vises backend'ens
     titel med pladsholderen som billede og uden pris. Backend'en sender IKKE et
     match-label eller en rating pr. produkt — kun det første kort får
     derfor en etiket ("Bedste match", editerbar via search_best_label), og
     der vises ALDRIG en opdigtet stjerne-rating. Skal backend'en en dag
     sende sådanne felter, læses de her ("badge"/"match_reason"/"rating"/
     "review_count") — indtil da er linjerne fraværende, ikke tomme. ─── */
  function renderSearchCard(product, node, index, queryId) {
    const handle = node ? node.handle : (product.handle || productIdOf(product));
    const url = '/products/' + handle;
    const title = node ? (node.title || '') : (product.product_title || product.title || '');
    const image = node && node.featuredImage && typeof node.featuredImage.url === 'string' ? node.featuredImage.url : '';
    const alt = (node && node.featuredImage && node.featuredImage.altText) || title;
    // Kun produktets egen pris. Uden node (timeout/fejl) ingen pris — backend'ens
    // er en kopi, der kan være lavere end den, kassen tager.
    const money = node && node.priceRange ? node.priceRange.minVariantPrice : null;
    const priceHtml = isDisplayOnly(node) ? 'Kun til visning' : (money ? fmtPrice(money.amount, money.currencyCode) : '');
    const pidStr = productIdOf(product) || String(product.product_id || product.id || '');
    const qidStr = queryId ? String(queryId) : '';
    // Aldrig src="": intet billede ⇒ pladsholderen (alt="" — den er pynt).
    const imgTag = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(alt)}" class="hv2-search-card-img" loading="lazy" width="500" height="500">`
      : `<img src="${escapeHtml(SEARCH_IMG_PLACEHOLDER)}" alt="" class="hv2-search-card-img" data-hv2-img="placeholder" loading="lazy" width="500" height="500">`;

    let label = '';
    if (index === 0) {
      label = `<span class="hv2-search-card-label hv2-search-card-label--best">${escapeHtml(LABELS.searchBest || 'Bedste match')}</span>`;
    } else if (product.badge || product.match_reason) {
      label = `<span class="hv2-search-card-label">${escapeHtml(product.badge || product.match_reason)}</span>`;
    }

    let rating = '';
    if (product.rating && product.review_count) {
      rating = `<span class="hv2-search-card-rating"><span class="hv2-star">★</span> ${escapeHtml(String(product.rating))} (${escapeHtml(String(product.review_count))})</span>`;
    }

    return `
      <a href="${escapeHtml(url)}" class="hv2-search-card"
         data-track-product="true" data-product-id="${escapeHtml(pidStr)}" data-position="${index + 1}" data-query-id="${escapeHtml(qidStr)}">
        ${imgTag}
        ${label}
        <span class="hv2-search-card-name">${escapeHtml(title)}</span>
        <span class="hv2-search-card-bottom">
          <span class="hv2-search-card-price">${priceHtml}</span>
          ${rating}
        </span>
      </a>`;
  }

  function renderCardSkeleton(count) {
    let html = '';
    for (let i = 0; i < count; i++) {
      html += `
        <div class="hv2-search-card" style="pointer-events:none;">
          <div class="hv2-search-card-img" style="background:var(--hv2-skeleton);animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
          <div style="height:13px;width:80%;background:var(--hv2-skeleton);border-radius:4px;animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
          <div style="height:16px;width:50%;background:var(--hv2-skeleton);border-radius:4px;animation:hv2Pulse 1.5s ease-in-out infinite;"></div>
        </div>`;
    }
    return html;
  }

  // Storefront API'et sender beløbet som decimal-streng ("349.0") + sin egen
  // valuta (currencyCode).
  function fmtPrice(p, currency) {
    const n = parseFloat(p);
    if (!isFinite(n)) return '';
    try {
      return n.toLocaleString('da-DK', { style: 'currency', currency: currency || CONFIG.currency, minimumFractionDigits: 2 });
    } catch (e) {
      return n.toLocaleString('da-DK', { minimumFractionDigits: 2 }) + ' kr';
    }
  }

  /* ─── Click Tracking ─── */
  function trackClick(productId, position, queryId) {
    const data = { query_id: queryId, product_id: productId, click_position: position, session_id: getSessionId(), query: state.searchQuery };
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${CONFIG.apiUrl}/api/analytics/search-click`, new Blob([JSON.stringify(data)], { type: 'application/json' }));
    } else {
      fetch(`${CONFIG.apiUrl}/api/analytics/search-click`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data), keepalive: true }).catch(() => {});
    }
    stashPendingSearchAttribution(queryId);
  }

  /* ─── Søge→ordre-attribution (kurv-attribut, se lib/search-attribution.ts) ───
     Denne del gik tabt da header-26 blev skrevet fra bunden 11 minutter efter
     45978eb6 landede i header-v2.js — samme fil, samme funktion, glemt i den
     parallelle omskrivning. Uden den bliver klik logget, men aldrig bundet
     til en ordre: Konverteringer forbliver 0 uanset hvor mange der køber. */
  function stashPendingSearchAttribution(queryId) {
    if (!queryId) return;
    try {
      localStorage.setItem('hv2_search_attr', JSON.stringify({ v: `${getSessionId()}|${queryId}|${Date.now()}`, pending: true }));
    } catch (e) { /* ignore */ }
  }

  function flushPendingSearchAttribution() {
    if (!window.routes || !window.routes.cart_update_url) return;
    let stashed;
    try {
      stashed = JSON.parse(localStorage.getItem('hv2_search_attr') || 'null');
    } catch (e) {
      return;
    }
    if (!stashed || !stashed.pending || !stashed.v) return;
    try {
      localStorage.setItem('hv2_search_attr', JSON.stringify({ v: stashed.v, pending: false }));
    } catch (e) { /* ignore */ }
    fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { jmd_search: stashed.v } }),
    }).catch(() => {});
  }

  /* Klik på den native /search-siden (Enter/"Se alle") er usynlige for
     dropdownens data-track-product-lyttere, fordi grid'et der ikke er
     bygget af performSearch(). Delegeret klik-lytter på #product-grid
     (samme id som main-search.liquid renderer) + ét søge-kald for at
     kende query_id (10-min-dedupen i lib/search-log.ts forhindrer en
     dobbelt logrække). */
  function initNativeSearchTracking() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    if (grid.dataset.hv2Tracked) return;
    grid.dataset.hv2Tracked = '1';

    const params = new URLSearchParams(window.location.search);
    const q = (params.get('q') || '').trim();
    if (!q) return;

    let nativeSearchQueryId = null;
    fetchSearchResults(q).then(result => {
      const qid = result && result.data && result.data.debug ? result.data.debug.query_id : null;
      if (qid) nativeSearchQueryId = qid;
    }).catch(() => {});

    grid.addEventListener('click', e => {
      const link = e.target.closest('a[data-product-id]');
      if (!link || !grid.contains(link)) return;
      const cards = Array.from(grid.querySelectorAll('.product-item__wrapper'));
      const card = link.closest('.product-item__wrapper');
      const position = card ? cards.indexOf(card) + 1 : 0;
      trackClick(link.dataset.productId, position, nativeSearchQueryId);
    });
  }

  /* ─── Mega menu (N kategorier, hver sin dropdown) ─── */
  /* Panelet er en SØSKENDE af <nav> (ikke en efterkommer af sin <li> — se
     snippets/hv26-nav-item.liquid for hvorfor), så det skal findes via
     toggle-knappens aria-controls og få sin "open"-klasse direkte, ikke
     via en ancestor-selector på .hv2-nav-item. */
  function getPanelFor(item) {
    const toggle = item.querySelector('[data-hv2-mega-toggle]');
    const id = toggle && toggle.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function closeAllMega(exceptItem) {
    el.megaItems.forEach(item => {
      if (item === exceptItem) return;
      item.classList.remove('hv2-mega-open');
      const toggle = item.querySelector('[data-hv2-mega-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
      const panel = getPanelFor(item);
      if (panel) panel.classList.remove('open');
    });
  }

  function openMega(item, opts) {
    if (!item) return;
    const silent = opts && opts.silent;
    if (!silent) closeSearch();
    closeAllMega(item);
    item.classList.add('hv2-mega-open');
    const toggle = item.querySelector('[data-hv2-mega-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    const panel = getPanelFor(item);
    if (panel) panel.classList.add('open');
    if (el.backdrop) el.backdrop.classList.add('active');
    state.openMegaItem = item;
  }

  function closeMega(item) {
    const target = item || state.openMegaItem;
    if (!target) return;
    target.classList.remove('hv2-mega-open');
    const toggle = target.querySelector('[data-hv2-mega-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    const panel = getPanelFor(target);
    if (panel) panel.classList.remove('open');
    if (state.openMegaItem === target) {
      state.openMegaItem = null;
      state.megaOpenedByHover = false;
      if (el.backdrop && !state.isSearchOpen) el.backdrop.classList.remove('active');
    }
  }

  function closeAllMegaHard() {
    el.megaItems.forEach(closeMega);
    state.openMegaItem = null;
    state.megaOpenedByHover = false;
  }

  function initMegaMenu() {
    if (el.megaItems.length === 0) return;

    var supportsPointer = typeof window.PointerEvent !== 'undefined';
    var enterEvt = supportsPointer ? 'pointerenter' : 'mouseenter';
    var leaveEvt = supportsPointer ? 'pointerleave' : 'mouseleave';

    function clearTimers() {
      if (state.megaOpenTimer) { clearTimeout(state.megaOpenTimer); state.megaOpenTimer = null; }
      if (state.megaCloseTimer) { clearTimeout(state.megaCloseTimer); state.megaCloseTimer = null; }
    }

    el.megaItems.forEach(item => {
      const toggle = item.querySelector('[data-hv2-mega-toggle]');
      const link = item.querySelector('.hv2-nav-link');
      const hasPanel = item.classList.contains('hv2-has-panel');

      if (!hasPanel || !toggle) {
        // Rent link uden panel — ingen hover-håndtering nødvendig.
        return;
      }

      item.addEventListener(enterEvt, e => {
        if (supportsPointer && e.pointerType !== 'mouse') return;
        clearTimers();
        // Instant hvis et ANDET panel allerede er åbent (diagonal
        // museflytning gennem naboelementer skal ikke give et blink) —
        // kun det ALLERFØRSTE åbne panel venter på hover-intent.
        const delay = state.openMegaItem && state.openMegaItem !== item
          ? CONFIG.megaSwitchDelay
          : CONFIG.megaOpenDelay;
        state.megaOpenTimer = setTimeout(() => {
          openMega(item);
          state.megaOpenedByHover = true;
        }, delay);
      });
      item.addEventListener(leaveEvt, e => {
        if (supportsPointer && e.pointerType !== 'mouse') return;
        if (state.megaOpenTimer) { clearTimeout(state.megaOpenTimer); state.megaOpenTimer = null; }
        state.megaCloseTimer = setTimeout(() => closeMega(item), CONFIG.megaCloseDelay);
      });

      /* Panelet er en SØSKENDE af <li> (se getPanelFor ovenfor), så det får
         sine EGNE hover-lyttere — uden dem lukker panelet altid 120ms efter
         museaflytning fra selve linket, uanset at musen allerede sidder inde
         i det åbne panel (det var selve rapporterede bug). */
      const panel = getPanelFor(item);
      if (panel) {
        panel.addEventListener(enterEvt, e => {
          if (supportsPointer && e.pointerType !== 'mouse') return;
          clearTimers();
        });
        panel.addEventListener(leaveEvt, e => {
          if (supportsPointer && e.pointerType !== 'mouse') return;
          state.megaCloseTimer = setTimeout(() => closeMega(item), CONFIG.megaCloseDelay);
        });
      }

      /* Toggle-knappen (usynlig indtil :focus-visible, se CSS) er
         disclosure-mønstret for tastatur — INGEN åbn-på-focus på selve
         linket, ellers skal en tastaturbruger tabbe igennem hvert panels
         links for at nå næste kategori. */
      toggle.addEventListener('click', e => {
        e.preventDefault();
        if (state.openMegaItem === item) {
          closeMega(item);
        } else {
          openMega(item);
        }
      });

      /* Toucn/pen (ikke mus): første tap åbner panelet og forhindrer
         navigation, andet tap på det ALLEREDE åbne link navigerer normalt
         (ingen preventDefault). Dette gælder kun linket selv, ikke
         toggle-knappen, som ALTID kun åbner/lukker. */
      if (link) {
        link.addEventListener('click', e => {
          if (supportsPointer && e.pointerType === 'mouse') return; // rigtig museklik navigerer altid
          if (state.openMegaItem !== item) {
            e.preventDefault();
            clearTimers();
            openMega(item);
          }
        });
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape' || !state.openMegaItem) return;
      const openItem = state.openMegaItem;
      const toggle = openItem.querySelector('[data-hv2-mega-toggle]');
      closeMega(openItem);
      if (toggle) toggle.focus();
    });
  }

  /* ─── Søgedropdown ─── */
  /* Visningen følger feltets INDHOLD, ikke om panelet lige er blevet åbnet.
     Tidligere tvang hver åbning tom-tilstanden frem: på en tablet fjernede
     kunden tastaturet, panelet lukkede, og når feltet blev trykket igen,
     stod søgeordet der stadig — men panelet viste "Populære søgninger" i
     stedet for resultaterne, så alt skulle tastes forfra (2026-09-26). */
  function openSearch() {
    if (!el.searchDropdown) return;
    closeAllMegaHard();
    el.searchDropdown.classList.add('open');
    if (el.backdrop) el.backdrop.classList.add('active');
    state.isSearchOpen = true;
    const query = el.searchInput ? el.searchInput.value.trim() : '';
    if (query.length >= CONFIG.minChars) {
      if (query === state.shownQuery) {
        showSearchView('search');
      } else {
        runSearch(query);
      }
    } else {
      showSearchView('default');
      renderEmptyState();
    }
  }

  // Nulstiller IKKE visningen — resultaterne og "Se alle" bevares til næste
  // åbning. Esc rydder feltet (og dermed visningen) før den lukker.
  function closeSearch() {
    if (!el.searchDropdown) return;
    el.searchDropdown.classList.remove('open');
    if (el.backdrop && !state.openMegaItem) el.backdrop.classList.remove('active');
    state.isSearchOpen = false;
  }

  function showSearchView(view) {
    if (el.defaultView) el.defaultView.style.display = view === 'default' ? '' : 'none';
    if (el.searchView) el.searchView.style.display = view === 'search' ? '' : 'none';
    if (view === 'default') updateSeeAll(0, '');
  }

  function syncHasValue() {
    if (!el.searchInner || !el.searchInput) return;
    el.searchInner.classList.toggle('hv2-has-value', el.searchInput.value.length > 0);
  }

  /* ─── Search Events ─── */
  let debounceTimer;

  function runSearch(query) {
    invalidateSearch();
    state.shownQuery = query;
    showSearchView('search');
    if (el.productGrid) el.productGrid.innerHTML = renderCardSkeleton(4);

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      addToHistory(query);
      performSearch(query, el.productGrid, el.resultsCount, el.ddHead, el.ddEyebrow, el.interpretation);
    }, CONFIG.searchDebounce);
  }

  function initSearchEvents() {
    if (!el.searchInput) return;

    // Klon inputtet for at fjerne eventuelle arvede theme-listeners.
    const clone = el.searchInput.cloneNode(true);
    el.searchInput.parentNode.replaceChild(clone, el.searchInput);
    el.searchInput = clone;

    el.searchInput.addEventListener('mousedown', e => e.stopPropagation());
    el.searchInput.addEventListener('focus', e => { e.stopPropagation(); openSearch(); });
    el.searchInput.addEventListener('click', e => { e.stopPropagation(); if (!state.isSearchOpen) openSearch(); });

    el.searchInput.addEventListener('input', e => {
      e.stopPropagation();
      syncHasValue();
      const query = e.target.value.trim();
      state.searchQuery = query;

      if (query.length >= CONFIG.minChars) {
        runSearch(query);
      } else {
        // Feltet er tømt: hverken en ventende debounce eller et svar på vej
        // må tegne resultater for et ord, der ikke længere står der.
        clearTimeout(debounceTimer);
        invalidateSearch();
        state.shownQuery = '';
        showSearchView('default');
        renderEmptyState();
      }
    });

    el.searchInput.addEventListener('keydown', e => {
      e.stopPropagation();
      if (!state.isSearchOpen) return;
      if (e.key === 'Escape') {
        // Ryd værdien EKSPLICIT (og synkronisér hv2-has-value med det
        // samme) i stedet for at stole på type=search's indbyggede Escape-
        // ryd-adfærd — den fyrer (hvis overhovedet) EFTER blur() nedenfor,
        // hvilket lod pillen sidde fast i sin aktive/fokuserede stil med en
        // ikke-fungerende Ryd-knap resten af sessionen (design-fidelity-
        // audit 2026-09-17). Et rigtigt 'input'-event (ikke bare et direkte
        // værdi-sæt) fyrer BÅDE denne funktions egen syncHasValue-lytter OG
        // initSearchExtras' separate syncClear-lytter på Ryd-knappen — uden
        // det forblev selve Ryd-knappen synlig efter Escape, selvom feltet
        // korrekt blev tømt (fanget ved re-test af netop denne rettelse,
        // samme dag).
        el.searchInput.value = '';
        el.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        closeSearch();
        el.searchInput.blur();
      }
    });
  }

  /* ─── Backdrop & Global Click ─── */
  /* Touch: det første tryk uden for søgningen, mens feltet har fokus, er
     kundens måde at fjerne skærmtastaturet på — IKKE at lukke søgningen
     (tablet-rapport 2026-09-26: "når jeg fjerner tastaturet, forsvinder
     alt, og jeg skal starte forfra"). Det tryk fjerner kun tastaturet;
     næste tryk udenfor lukker panelet. Et tryk på et link/en knap lukker
     som altid, og med mus/trackpad er intet ændret. Måles ved pointerdown,
     fordi fokus allerede har forladt feltet, når 'click' fyrer. */
  let keyboardDismissTap = false;

  function closeSearchFromOutside() {
    if (keyboardDismissTap) {
      if (el.searchInput) el.searchInput.blur();
      return;
    }
    closeSearch();
  }

  function initGlobalEvents() {
    document.addEventListener('pointerdown', e => {
      keyboardDismissTap =
        e.pointerType !== 'mouse' &&
        !!el.searchInput &&
        document.activeElement === el.searchInput &&
        !(e.target.closest && e.target.closest('a, button, input, select, textarea, label, [role="button"]'));
    }, true);
    // window bobler EFTER document-lytterne nedenfor, så flaget kun gælder
    // netop det klik, der fulgte trykket (aldrig et senere tastatur-klik).
    window.addEventListener('click', () => { keyboardDismissTap = false; });

    // Delegeret på document (ikke direkte på el.backdrop) med vilje: denne
    // funktion bindes kun ÉN gang (se `globalsBound` i init()), men
    // backdrop-noden udskiftes ved hver shopify:section:load i Theme
    // Editor — en direkte lytter på selve noden ville pege på et forladt
    // element efter første genindlæsning og aldrig fyre igen.
    document.addEventListener('click', e => {
      if (!e.target.closest('[data-hv2-backdrop]')) return;
      if (!e.target.closest('[data-hv2-search-dropdown]') && !e.target.closest('.hv2-search-wrap')) {
        closeSearchFromOutside();
      }
      if (!e.target.closest('.hv2-mega') && !e.target.closest('[data-hv2-mega-item]')) {
        closeAllMegaHard();
      }
    });

    document.addEventListener('click', e => {
      if (state.isSearchOpen && !e.target.closest('[data-hv2-search-dropdown]') && !e.target.closest('.hv2-search-wrap')) {
        closeSearchFromOutside();
      }
      if (state.openMegaItem && !e.target.closest('.hv2-mega') && !e.target.closest('[data-hv2-mega-item]')) {
        closeAllMegaHard();
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        if (state.isSearchOpen) closeSearch();
        if (state.mobileMenuOpen) closeMobileMenu();
      }

      // Cmd+K / Ctrl+K — matcher også Shift+K ('K'), som Shift/CapsLock giver.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        if (!isMobile() && el.searchInput) {
          el.searchInput.focus();
          openSearch();
        }
      }
    });
  }

  /* ─── Mobile Menu ─── */
  function openMobileMenu(searchMode) {
    if (!el.mobileOverlay) return;
    el.mobileOverlay.classList.toggle('hv2-search-entry', !!searchMode);
    el.mobileOverlay.classList.add('open');
    void el.mobileOverlay.offsetHeight;
    if (el.mobileMenuToggle) el.mobileMenuToggle.setAttribute('aria-expanded', 'true');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    state.mobileMenuOpen = true;
  }

  function closeMobileMenu() {
    if (!el.mobileOverlay) return;
    el.mobileOverlay.classList.remove('open');
    el.mobileOverlay.classList.remove('hv2-search-entry');
    if (el.mobileMenuToggle) el.mobileMenuToggle.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    state.mobileMenuOpen = false;
  }

  function initMobileMenu() {
    if (el.mobileMenuToggle) {
      el.mobileMenuToggle.addEventListener('click', e => {
        e.preventDefault();
        openMobileMenu();
      });
    }

    if (el.mobileClose) {
      el.mobileClose.addEventListener('click', e => {
        e.preventDefault();
        closeMobileMenu();
      });
    }

    if (el.mobileOverlay) {
      const cartCta = el.mobileOverlay.querySelector('.hv2-btn.hv2-btn--navy');
      if (cartCta) {
        cartCta.addEventListener('click', () => closeMobileMenu());
      }
    }

    // Accordion toggle — delegeret på overlayet, så en editor-genindlæsning
    // der bytter accordion-indholdet ud ikke kræver et nyt bind-kald.
    if (el.mobileOverlay && !el.mobileOverlay.dataset.hv26AccordionBound) {
      el.mobileOverlay.dataset.hv26AccordionBound = '1';
      el.mobileOverlay.addEventListener('click', e => {
        const btn = e.target.closest('.hv2-mobile-item[data-has-children] > .hv2-mobile-btn');
        if (!btn) return;
        e.preventDefault();
        const item = btn.closest('.hv2-mobile-item');
        const submenu = item.querySelector('.hv2-mobile-sub');
        if (!submenu) return;
        const isExpanded = item.classList.contains('expanded');
        el.mobileOverlay.querySelectorAll('.hv2-mobile-item.expanded').forEach(other => {
          other.classList.remove('expanded');
          const sub = other.querySelector('.hv2-mobile-sub');
          if (sub) sub.classList.remove('open');
          const otherBtn = other.querySelector('.hv2-mobile-btn');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        });
        if (!isExpanded) {
          item.classList.add('expanded');
          submenu.classList.add('open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    }

    // Mobil søgning — deler `performSearch` (og dermed dens AbortController)
    // med desktop-dropdownen. Kun ét af de to overlays kan være synligt ad
    // gangen, så delt annullering er ikke et problem.
    let mobileDebounce;

    if (el.mobileSearchInput) {
      el.mobileSearchInput.addEventListener('input', e => {
        const query = e.target.value.trim();
        state.searchQuery = query;

        invalidateSearch(); // som runSearch() — se invalidateSearch()
        if (query.length >= CONFIG.minChars) {
          if (el.mobileDefaultView) el.mobileDefaultView.style.display = 'none';
          if (el.mobileSearchView) el.mobileSearchView.style.display = '';
          if (el.mobileResultsContainer) el.mobileResultsContainer.innerHTML = renderCardSkeleton(4);

          clearTimeout(mobileDebounce);
          mobileDebounce = setTimeout(() => {
            addToHistory(query);
            performSearch(query, el.mobileResultsContainer, el.mobileResultsCount, el.mobileDdHead, el.mobileDdEyebrow, el.mobileInterpretation);
          }, CONFIG.searchDebounce);
        } else {
          clearTimeout(mobileDebounce);
          if (el.mobileDefaultView) el.mobileDefaultView.style.display = '';
          if (el.mobileSearchView) el.mobileSearchView.style.display = 'none';
        }
      });

      el.mobileSearchInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const q = e.target.value.trim();
          if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
        }
      });
    }
  }

  /* Mobil søge-trigger (fake felt under hovedrækken) → åbn overlay + fokusér søgefelt */
  function initMobileTrigger() {
    if (!el.searchTrigger) return;
    el.searchTrigger.addEventListener('click', e => {
      e.preventDefault();
      openMobileMenu(true);
      if (!el.mobileSearchInput) return;
      // To modstridende krav, begge målt/dokumenterede, samme kald begge
      // veje: iOS Safari kræver et SYNKRONT focus() inde i klik-handleren
      // for at åbne tastaturet; Chrome flytter først reelt fokus efter
      // mindst ét fuldført maleri (dobbelt requestAnimationFrame). Et
      // ekstra focus()-kald på et allerede fokuseret element er et no-op.
      el.mobileSearchInput.focus();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.mobileSearchInput.focus();
        });
      });
    });
  }

  /* ─── Sticky ─── */
  /* Bindes kun ÉN gang (kaldes fra den globals-gaterede del af init()) —
     men læser `el.header` og dens data-hv2-sticky-attribut FRISK ved hver
     scroll-tick i stedet for at capture dem ved bind-tidspunktet, så en
     Theme Editor-ændring af sticky-typen (som udskifter <header>-noden via
     shopify:section:load) stadig slår igennem uden en fuld sideindlæsning. */
  function initStickySystem() {
    let lastY = window.scrollY;
    let ticking = false;

    function update() {
      if (!el.header) { ticking = false; return; }
      const type = el.header.getAttribute('data-hv2-sticky');
      const y = window.scrollY;
      el.header.classList.toggle('scrolled', y > 10);
      if (type === 'on-scroll-up') {
        if (y > lastY && y > 120) {
          el.header.classList.add('hv2-hide');
        } else {
          el.header.classList.remove('hv2-hide');
        }
      }
      lastY = y;
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
  }

  /* ─── Animated Placeholder ─── */
  let placeholders = ['Søg efter produkter...'];
  let placeholderInterval = 4000;
  let currentPHIndex = 0;
  let phAnimInterval = null;
  let isAnimating = false;
  let animTimeout = null;
  let animRunId = 0;

  function typeText(element, text, cb) {
    if (!element || !text) return;
    const run = ++animRunId;
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = true;
    let i = 0;
    element.textContent = '';
    (function type() {
      if (run !== animRunId) return;
      if (i < text.length) {
        element.textContent += text.charAt(i);
        i++;
        animTimeout = setTimeout(type, 40);
      } else {
        animTimeout = null;
        isAnimating = false;
        if (cb) cb();
      }
    })();
  }

  function deleteText(element, cb) {
    if (!element) return;
    const run = ++animRunId;
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = true;
    let text = element.textContent;
    (function del() {
      if (run !== animRunId) return;
      if (text.length > 0) {
        text = text.slice(0, -1);
        element.textContent = text;
        animTimeout = setTimeout(del, 25);
      } else {
        animTimeout = null;
        isAnimating = false;
        if (cb) cb();
      }
    })();
  }

  function cyclePlaceholder() {
    if (!el.animatedPlaceholder || placeholders.length === 0) return;
    if (el.searchInput && el.searchInput.value.length > 0) return;
    if (isAnimating) return;

    deleteText(el.animatedPlaceholder, () => {
      currentPHIndex = (currentPHIndex + 1) % placeholders.length;
      typeText(el.animatedPlaceholder, placeholders[currentPHIndex]);
    });
  }

  function startPHAnimation() {
    if (phAnimInterval || placeholders.length <= 1) return;
    phAnimInterval = setInterval(cyclePlaceholder, placeholderInterval);
  }

  function stopPHAnimation() {
    animRunId++;
    if (phAnimInterval) { clearInterval(phAnimInterval); phAnimInterval = null; }
    if (animTimeout) { clearTimeout(animTimeout); animTimeout = null; }
    isAnimating = false;
  }

  function initAnimatedPlaceholder() {
    if (!el.animatedPlaceholder || placeholders.length === 0) return;
    typeText(el.animatedPlaceholder, placeholders[0], () => {
      setTimeout(startPHAnimation, placeholderInterval);
    });

    if (el.searchInput) {
      const toggle = () => {
        if (el.searchInput.value.length > 0) {
          el.animatedPlaceholder.style.display = 'none';
          stopPHAnimation();
        } else {
          el.animatedPlaceholder.style.display = '';
          startPHAnimation();
        }
      };
      el.searchInput.addEventListener('input', toggle);
      el.searchInput.addEventListener('change', toggle);
      toggle();
    }
  }

  /* ─── Trending Searches (API) ─── */
  function fetchTrending() {
    try {
      const cached = JSON.parse(sessionStorage.getItem('hv2_trending') || 'null');
      if (cached && cached.terms && cached.terms.length > 0 && Date.now() - cached.ts < 300000) {
        state.trendingSearches = cached.terms;
        return;
      }
    } catch (e) { /* ignore */ }

    fetch(`${CONFIG.apiUrl}/api/analytics/trending-searches`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(result => {
        const terms = result.data;
        if (Array.isArray(terms) && terms.length > 0) {
          state.trendingSearches = terms;
          sessionStorage.setItem('hv2_trending', JSON.stringify({ terms, ts: Date.now() }));
        }
      })
      .catch(() => { /* behold fallback fra Trending Keyword-blokkene */ });
  }

  /* ─── Cart Badge Observer ─── */
  function initCartBadge() {
    const badge = document.querySelector('.hv2-cart-badge');
    if (!badge) return;

    function updateVisibility() {
      const count = parseInt(badge.textContent, 10);
      badge.style.display = count > 0 ? '' : 'none';
    }

    updateVisibility();

    const observer = new MutationObserver(updateVisibility);
    observer.observe(badge, { childList: true, characterData: true, subtree: true });
  }

  /* ─── --height-header ─── */
  function fixHeaderHeightVar() {
    if (!el.header) return;
    function apply() {
      var h = el.header.offsetHeight;
      if (h > 0 && h < 500) {
        document.body.style.setProperty('--height-header', h + 'px');
      }
    }
    apply();
    window.addEventListener('load', apply);
    // Header-26 kan ændre højde uden et scroll/resize-event (fx mega-panel
    // åbner/lukker ikke, MEN kategori-rækkens indhold kan wrappe ved
    // skrifttype-indlæsning, og Theme Editor kan ændre padding-settings
    // live) — en ResizeObserver fanger det header-v2.js' 'load'-only-fix
    // ikke gjorde.
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(apply);
      ro.observe(el.header);
    }
  }

  function updateSeeAll(count, query) {
    const label = LABELS.searchSeeAll || 'Se alle';
    [el.seeAll, el.mobileSeeAll].forEach(seeAll => {
      if (!seeAll) return;
      if (count > 0 && query) {
        seeAll.textContent = label + ' →';
        seeAll.href = '/search?q=' + encodeURIComponent(query);
        seeAll.hidden = false;
      } else {
        seeAll.hidden = true;
      }
    });
  }

  function initSearchExtras() {
    if (!el.searchInput) return;

    const clearBtn = document.querySelector('[data-hv2-search-clear]');
    if (clearBtn) {
      const syncClear = () => { clearBtn.hidden = !(el.searchInput.value.length > 0); };
      el.searchInput.addEventListener('input', syncClear);
      syncClear();
      clearBtn.addEventListener('click', e => {
        e.stopPropagation();
        el.searchInput.value = '';
        el.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        el.searchInput.focus();
      });
    }

    el.searchInput.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const q = el.searchInput.value.trim();
        if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
      }
    });

    const searchBtn = document.querySelector('.hv2-search-btn');
    if (searchBtn) {
      searchBtn.addEventListener('click', () => {
        const q = el.searchInput.value.trim();
        if (q) {
          window.location.href = '/search?q=' + encodeURIComponent(q);
        } else {
          el.searchInput.focus();
        }
      });
    }
  }

  document.addEventListener('click', e => {
    const link = e.target.closest('a[data-track-product]');
    if (link) trackClick(link.dataset.productId, parseInt(link.dataset.position, 10), link.dataset.queryId || null);
  });

  /* ─── Theme Editor: åbn + pin panelet/accordion-rækken der indeholder den
     valgte blok. shopify:block:select genfyrer efter hver keystroke i
     panelet (load:true) — uden pinning ville hover-leave/outside-click
     lukke panelet midt i en redigering. ─── */
  function findAncestorMegaItem(node) {
    let n = node;
    while (n && n !== document.body) {
      if (n.matches && n.matches('[data-hv2-mega-item]')) return n;
      if (n.matches && n.matches('.hv2-mobile-item')) return n;
      if (n.matches && n.matches('.hv2-mega')) {
        // Panelet er en søskende af sin <li>, ikke en efterkommer (se
        // snippets/hv26-nav-item.liquid) — hop over til <li>'en via det
        // delte item-id i stedet for at fortsætte opad i DOM'et.
        const itemId = n.getAttribute('data-hv2-item');
        return itemId ? document.querySelector('[data-hv2-mega-item][data-hv2-block="' + itemId + '"]') : null;
      }
      n = n.parentElement;
    }
    return null;
  }

  function initEditorEvents() {
    if (!window.Shopify || !Shopify.designMode) return;

    document.addEventListener('shopify:block:select', e => {
      const blockId = e.detail && e.detail.blockId;
      if (!blockId) return;
      const target = document.querySelector('[data-hv2-block="' + blockId + '"]');
      if (!target) return;
      state.pinnedBlockId = blockId;

      const megaItem = findAncestorMegaItem(target);
      if (!megaItem) return;

      if (megaItem.matches('[data-hv2-mega-item]')) {
        if (megaItem.classList.contains('hv2-has-panel')) {
          openMega(megaItem, { silent: true });
        }
      } else {
        // Mobil accordion-række — kræver overlayet åbent for at være synligt.
        openMobileMenu();
        const submenu = megaItem.querySelector('.hv2-mobile-sub');
        const btn = megaItem.querySelector('.hv2-mobile-btn');
        if (submenu) {
          document.querySelectorAll('.hv2-mobile-item.expanded').forEach(o => {
            if (o !== megaItem) {
              o.classList.remove('expanded');
              const s = o.querySelector('.hv2-mobile-sub');
              if (s) s.classList.remove('open');
            }
          });
          megaItem.classList.add('expanded');
          submenu.classList.add('open');
          if (btn) btn.setAttribute('aria-expanded', 'true');
        }
      }
    });

    document.addEventListener('shopify:block:deselect', e => {
      const blockId = e.detail && e.detail.blockId;
      if (blockId && state.pinnedBlockId === blockId) {
        state.pinnedBlockId = null;
      }
    });
  }

  /* ─── Init ───
     shopify:section:load kalder init() igen for hver gentegning af
     sektionen i Theme Editor (nødvendigt: DOM'en er udskiftet, elementer
     skal caches og item-scopede listeners rebindes på de NYE noder — det
     er IKKE et leak, fordi de GAMLE noder og deres listeners forsvinder
     med den gamle DOM). Men document/window-listeners (klik, keydown,
     scroll, shopify:block:select) sidder på objekter der IKKE genskabes —
     dem må kun bindes ÉN gang, ellers dobler/tripler de sig ved hver
     genindlæsning (kendt fragilitet i header-v2.js, se
     docs/HEADER_FOOTER_2026_09.md). `globalsBound` er den vagt. */
  let globalsBound = false;

  function init() {
    LABELS = (window.headerV2Settings && window.headerV2Settings.labels) || {};
    placeholders = (window.headerV2Settings && window.headerV2Settings.animatedPlaceholders) || ['Søg efter produkter...'];
    placeholderInterval = ((window.headerV2Settings && window.headerV2Settings.placeholderInterval) || 4) * 1000;
    state.trendingSearches = (window.headerV2Settings && window.headerV2Settings.trendingSearches) || [];

    cacheElements();
    state.shownQuery = ''; // ny DOM (fx Theme Editor-genindlæsning) = tomt grid
    sweepSearchImages(); // Liquid-billeder, der fejlede før lytteren fandtes
    fixHeaderHeightVar();
    initSearchEvents();
    initMegaMenu();
    initMobileMenu();
    initAnimatedPlaceholder();
    initCartBadge();
    fetchTrending();
    initSearchExtras(); // SKAL køre efter initSearchEvents (clone-node)
    initMobileTrigger();

    if (!globalsBound) {
      globalsBound = true;
      initGlobalEvents();
      initStickySystem();
      initEditorEvents();
      flushPendingSearchAttribution();
      initNativeSearchTracking();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', e => {
    if (e.target && e.target.querySelector && e.target.querySelector('.header-v2')) {
      init();
    }
  });
})();
